import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useAuthStore, useStudioStore } from '@/store'
import { callAuth } from '@/lib/authClient'
import { mapRawStudiosToStore } from '@/lib/studioMapper'
import { formatAuthErrorMessage } from '@/lib/authErrorMessages'
import { Card } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { CheckCircle2, Loader2, Smartphone } from 'lucide-react'

type Props = {
  className?: string
  title?: string
}

export function TelegramLinkCard({ className = '', title = 'Telegram для входа' }: Props) {
  const { user, login } = useAuthStore()
  const { setStudios } = useStudioStore()
  const widgetHostRef = useRef<HTMLDivElement | null>(null)
  const [isLinking, setIsLinking] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [widgetReady, setWidgetReady] = useState(false)
  const reactId = useId()
  const callbackName = useMemo(() => `__telegramLinkAuth_${reactId.replace(/[^a-zA-Z0-9_]/g, '_')}`, [reactId])
  const windowWithDynamicFields = window as unknown as Window & Record<string, unknown>

  useEffect(() => {
    if (user?.telegram_id || !widgetHostRef.current) return

    const host = widgetHostRef.current
    host.innerHTML = ''
    setWidgetReady(false)

    const handleTelegramAuth = async (widgetUser: Record<string, unknown>) => {
      setIsLinking(true)
      setLinkError(null)

      try {
        const widgetData: Record<string, unknown> = {
          id: widgetUser.id,
          first_name: widgetUser.first_name,
          last_name: widgetUser.last_name,
          username: widgetUser.username,
          photo_url: widgetUser.photo_url,
          auth_date: widgetUser.auth_date,
          hash: widgetUser.hash
        }

        const result = await callAuth('widget', {
          widgetData,
          link_current_user: true
        }, {
          useUserJwt: true
        })

        localStorage.setItem('auth_token', result.token)
        setStudios(mapRawStudiosToStore(result.studios || []))
        login({
          ...result.user,
          created_at: (result.user as any).created_at || user?.created_at || new Date().toISOString(),
          updated_at: (result.user as any).updated_at || new Date().toISOString(),
          studios: result.studios || []
        } as any)
      } catch (error) {
        setLinkError(formatAuthErrorMessage(error, 'telegram'))
      } finally {
        setIsLinking(false)
      }
    }

    windowWithDynamicFields[callbackName] = handleTelegramAuth

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.async = true
    script.setAttribute('data-telegram-login', import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'your_bot_name')
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-userpic', 'false')
    script.setAttribute('data-request-access', 'write')
    script.setAttribute('data-onauth', `${callbackName}(user)`)
    script.onload = () => setWidgetReady(true)
    host.appendChild(script)

    return () => {
      host.innerHTML = ''
      delete windowWithDynamicFields[callbackName]
    }
  }, [callbackName, login, setStudios, user, windowWithDynamicFields])

  return (
    <Card className={`p-4 ${className}`.trim()}>
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-base">{title}</h3>
      </div>

      {user?.telegram_id ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-700 text-sm">
            Привязан Telegram ID: <span className="font-medium">{user.telegram_id}</span>
          </span>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3">
            Если вы вошли по email, можно привязать Telegram и потом входить обоими способами.
          </p>

          <div className="min-h-[44px] flex items-center justify-center sm:justify-start">
            <div ref={widgetHostRef} />
            {!widgetReady && !isLinking && (
              <Button size="sm" variant="outline" disabled>
                Загрузка Telegram...
              </Button>
            )}
          </div>

          {isLinking && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Привязываем Telegram...</span>
            </div>
          )}

          {linkError && (
            <p className="mt-3 text-sm text-red-600">{linkError}</p>
          )}
        </>
      )}
    </Card>
  )
}
